import React, { useState, useEffect } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination, Grid, Switch } from '@mui/material';
import moment from 'moment';
import _ from 'lodash';
import Sidebar from '../../components/Sidebar/Sidebar';
import DateRangePicker from '../../components/DateRangePicker/DateRangePicker';
import { searchTicketsByDate } from '../../utils/apiUtils';
import './CSAT.scss';

const CSATPage = () => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [page, setPage] = useState(0);
  const [filterAgent, setFilterAgent] = useState('');
  const [filterApplied, setFilterApplied] = useState(false);
  const rowsPerPage = 30;

  const formatDate = (date) => {
    return date ? moment(date).format('DD-MM-YYYY HH:mm') : '';
  };

  useEffect(() => {
    const fetchTicketsByDate = async () => {
      if (startDate && endDate) {
        try {
          let tickets = await searchTicketsByDate(startDate, endDate);
          tickets = tickets.filter(ticket => ticket.ticket_owner_id !== null);
          setTickets(tickets);
          setFilteredTickets(tickets.filter(ticket => ticket.ticket_score !== null));
        } catch (error) {
          console.error('Error fetching tickets:', error);
        }
      }
    };

    fetchTicketsByDate();
  }, [startDate, endDate]);

  const handleStartDateChange = (date) => {
    setFilterApplied(false);
    setStartDate(date);
  };
  
  const handleEndDateChange = (date) => {
    setFilterApplied(false);
    setEndDate(date);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleFilterAgent = (agent) => {
    if (filterAgent === agent) {
      setFilterAgent('');
      setFilterApplied(false);
    } else {
      setFilterAgent(agent);
      setFilterApplied(true);
    }
    setPage(0); // Reset the page when applying or removing the filter
  };

  const calculateAllAgentScores = () => {
    const ticketsByAgent = _.groupBy(filteredTickets, 'ticket_owner_name');
    const teamTickets = filteredTickets.filter(ticket => ticket.ticket_owner_id !== null && ticket.ticket_owner_name !== null);
    const teamScores = teamTickets.map(ticket => ticket.ticket_score);
    const teamTotalReviews = teamScores.length;
    const teamPositiveReviews = teamScores.filter(score => score >= 4).length;
    const teamNeutralReviews = teamScores.filter(score => score === 3).length;
    const teamNegativeReviews = teamScores.filter(score => score <= 2).length;

    const teamPositivePercent = (teamPositiveReviews / teamTotalReviews) * 100;
    const teamNeutralPercent = (teamNeutralReviews / teamTotalReviews) * 100;
    const teamNegativePercent = (teamNegativeReviews / teamTotalReviews) * 100;

    const scoresByAgent = Object.entries(ticketsByAgent).map(([agent, agentTickets]) => {
      const scores = agentTickets.map(ticket => ticket.ticket_score);
      const totalReviews = scores.length;
      const positiveReviews = scores.filter(score => score >= 4).length;
      const neutralReviews = scores.filter(score => score === 3).length;
      const negativeReviews = scores.filter(score => score <= 2).length;

      const positivePercent = (positiveReviews / totalReviews) * 100;
      const neutralPercent = (neutralReviews / totalReviews) * 100;
      const negativePercent = (negativeReviews / totalReviews) * 100;

      return {
        agent,
        positivePercent: positivePercent.toFixed(2),
        neutralPercent: neutralPercent.toFixed(2),
        negativePercent: negativePercent.toFixed(2),
        ticketCount: totalReviews,
      };
    });

    return {
      scoresByAgent,
      teamScore: {
        agent: "Team",
        positivePercent: teamPositivePercent.toFixed(2),
        neutralPercent: teamNeutralPercent.toFixed(2),
        negativePercent: teamNegativePercent.toFixed(2),
        ticketCount: teamTotalReviews,
      },
    };
  };

  const { scoresByAgent, teamScore } = calculateAllAgentScores();
  const filteredAgentTickets = filterApplied ? filteredTickets.filter(ticket => ticket.ticket_owner_name === filterAgent) : filteredTickets;

  return (
    <Grid container justifyContent="center">
      <Sidebar />
      <Grid item xs={10}>
        <Grid container justifyContent="center">
          <Typography variant="h2" gutterBottom>CSAT</Typography>
        </Grid>
  
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Box className="StyledButtonContainer">
              <Box className="StyledCardHalf" style={{ height: "375px" }}>
                <Typography variant="h5">Date Range</Typography>
                <DateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  onStartDateChange={handleStartDateChange}
                  onEndDateChange={handleEndDateChange}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box className="StyledButtonContainer">
              <Box className="StyledCardHalf">
                <Typography variant="h5">CSAT Overview</Typography>
                <Grid container spacing={2} direction="column">
                  <Grid item container direction="row" justifyContent="space-between" style={{ borderBottom: "0.1px solid grey" }}>
                    <Typography variant="body2">Sent:</Typography>
                    <Typography variant="body2" className="count">{tickets.filter(ticket => ticket.feedback_sent !== null).length}</Typography>
                  </Grid>
                  <Grid item container direction="row" justifyContent="space-between" style={{ borderBottom: "0.1px solid grey" }}>
                    <Typography variant="body2">Received:</Typography>
                    <Typography variant="body2" className="count">{tickets.filter(ticket => ticket.feedback_received !== null).length}</Typography>
                  </Grid>
                  <Grid item container direction="row" justifyContent="space-between" style={{ borderBottom: "0.1px solid grey" }}>
                    <Typography variant="body2">Score:</Typography>
                    <Typography variant="body2" className="count">{tickets.filter(ticket => ticket.ticket_score !== null).length}</Typography>
                  </Grid>
                  <Grid item container direction="row" justifyContent="space-between" style={{ borderBottom: "0.1px solid grey" }}>
                    <Typography variant="body2">Total Tickets:</Typography>
                    <Typography variant="body2" className="count">{tickets.length}</Typography>
                  </Grid>
                  <Grid item container direction="row" justifyContent="space-between" style={{ borderBottom: "0.1px solid grey" }}>
                    <Typography variant="body2">Team Score: Positive:</Typography>
                    <Typography variant="body2" className="count">{teamScore.positivePercent}%</Typography>
                  </Grid>
                  <Grid item container direction="row" justifyContent="space-between" style={{ borderBottom: "0.1px solid grey" }}>
                    <Typography variant="body2">Neutral:</Typography>
                    <Typography variant="body2" className="count">{teamScore.neutralPercent}%</Typography>
                  </Grid>
                  <Grid item container direction="row" justifyContent="space-between" style={{ borderBottom: "0.1px solid grey" }}>
                    <Typography variant="body2">Negative:</Typography>
                    <Typography variant="body2" className="count">{teamScore.negativePercent}%</Typography>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Grid>
        </Grid>
  
        <Box mt={4}>
          <Typography variant="h5"  gutterBottom>Agent Scores</Typography>
          <TableContainer component={Paper} style={{ position: "relative", zIndex: "0" }} className="customTable">
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell>Agent</TableCell>
                  <TableCell>Positive (%)</TableCell>
                  <TableCell>Neutral (%)</TableCell>
                  <TableCell>Negative (%)</TableCell>
                  <TableCell>Out of</TableCell>
                  <TableCell>Apply Filter</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scoresByAgent.map(({ agent, positivePercent, neutralPercent, negativePercent, ticketCount }) => (
                  <TableRow key={agent}>
                    <TableCell>{agent}</TableCell>
                    <TableCell>{positivePercent}%</TableCell>
                    <TableCell>{neutralPercent}%</TableCell>
                    <TableCell>{negativePercent}%</TableCell>
                    <TableCell>{ticketCount}</TableCell>
                    <TableCell>
                      <Switch
                        checked={filterApplied && filterAgent === agent}
                        onChange={() => handleFilterAgent(agent)}
                        inputProps={{ 'aria-label': 'controlled' }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
  
        <Box mt={4}>
          <Typography variant="h5" gutterBottom>Filtered Tickets</Typography>
          <TableContainer component={Paper} className="customTable">
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell>Agent</TableCell>
                  <TableCell>Ticket ID</TableCell>
                  <TableCell>Ticket Name</TableCell>
                  <TableCell>Date Opened</TableCell>
                  <TableCell>Date Closed</TableCell>
                  <TableCell>Score</TableCell>
                  <TableCell>Feedback</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(filterApplied && rowsPerPage > 0
                  ? filteredAgentTickets.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  : filteredAgentTickets
                ).map((ticket) => (
                  <TableRow key={ticket.ticket_id}>
                    <TableCell>{ticket.ticket_owner_name}</TableCell>
                    <TableCell>{ticket.ticket_id}</TableCell>
                    <TableCell>{ticket.ticket_name}</TableCell>
                    <TableCell>{formatDate(ticket.ticket_created)}</TableCell>
                    <TableCell>{formatDate(ticket.ticket_closed)}</TableCell>
                    <TableCell>{ticket.ticket_score}</TableCell>
                    <TableCell>{ticket.ticket_feedback || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[30]}
              component="div"
              count={filteredAgentTickets.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
            />
          </TableContainer>
        </Box>
      </Grid>
    </Grid>
  );
  
  
};

export default CSATPage;